import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./InvoicePrintModal.module.css";
import {
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaPrint,
} from "react-icons/fa";

const InvoicePrintModal = ({ invoice, entity, onClose }) => {
  const { t, i18n } = useTranslation();
  const { userData, token } = useContext(UserContext);
  const isRTL = i18n.language === "ar";
  const previewRef = useRef(null);

  // Template structure with visibility toggles (matching Android)
  const getDefaultTemplate = () => {
    return {
      // Company Information from userData
      companyName: userData?.company?.companyName || userData?.companyName || "",
      companyAddress: userData?.company?.address || userData?.address || "",
      mobileNumber: userData?.company?.phoneNumber || userData?.phoneNumber || userData?.phone || "",
      taxNumber: userData?.company?.taxId || userData?.taxId || "",
      commercialNumber: userData?.company?.registrationNumber || userData?.registrationNumber || "",
      imageUrl: userData?.company?.logoUrl || userData?.logoUrl || "",
      currency: "EGP",

      // Visibility Toggles (Android defaults)
      isFooter: true,
      isTaxValue: true,  // Tax enabled by default like Android
      isPaymentDetails: true,
      isCompanyName: true,
      isCompanyAddress: true,
      isMobileNumber: true,
      isTaxNumber: true,
      isCommercialNumber: true,
      isImageUrl: false,
    };
  };

  const [template, setTemplate] = useState(getDefaultTemplate());

  // Load company data from API
  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      // Fetch tenant/company details from API
      if (userData?.Tenant?.id) {
        const response = await axios.get(`/api/tenant/${userData.Tenant.id}`, {
          headers: { Authorization: token },
        });
        const tenantData = response.data.items?.[0] || response.data;
        console.log("Fetched tenant data:", tenantData);

        // Update template with fetched tenant data
        setTemplate(prev => ({
          ...prev,
          companyName: tenantData.companyName || tenantData.name || prev.companyName,
          companyAddress: tenantData.address || tenantData.location || prev.companyAddress,
          mobileNumber: tenantData.contactPhone || tenantData.phone || prev.mobileNumber,
          taxNumber: tenantData.taxId || prev.taxNumber,
          commercialNumber: tenantData.registrationNumber || prev.commercialNumber,
          currency: tenantData.currency || prev.currency,
        }));
      } else if (userData?.Farm?.id) {
        // Fallback to farm data if available
        setTemplate(prev => ({
          ...prev,
          companyName: userData.Farm?.name || prev.companyName,
          companyAddress: userData.Farm?.address || prev.companyAddress,
          mobileNumber: userData.Farm?.phone || userData.phone || prev.mobileNumber,
        }));
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      // Use existing userData as fallback
      if (userData) {
        setTemplate(prev => ({
          ...prev,
          companyName: prev.companyName || userData.name || userData.username,
          mobileNumber: prev.mobileNumber || userData.phone,
          companyAddress: prev.companyAddress || userData.address,
        }));
      }
    }
  };


  // Toggle field visibility
  const toggleVisibility = (field) => {
    setTemplate(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Update template field
  const updateTemplateField = (field, value) => {
    setTemplate(prev => ({ ...prev, [field]: value }));
  };


  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(isRTL ? "ar-EG" : "en-US", {
      style: "currency",
      currency: template.currency || "EGP",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(
      isRTL ? "ar-EG" : "en-US",
      options
    );
  };

  // Calculate totals - Match InvoiceDetails component logic exactly
  const calculateRawSubtotal = () => {
    // Always prioritize calculating from items if they exist
    if (invoice.items && invoice.items.length > 0) {
      const itemsSubtotal = invoice.items.reduce((sum, item) => {
        const quantity = parseFloat(item.quantity || item.invoiceDetails?.quantity || 1);
        const unitPrice = parseFloat(item.pricePerUnit || item.invoiceDetails?.pricePerUnit || item.price || item.unitPrice || 0);
        const itemTotal = parseFloat(item.totalAmount || item.invoiceDetails?.totalAmount) || (quantity * unitPrice);
        return sum + itemTotal;
      }, 0);
      return itemsSubtotal;
    }

    // Only use these fallbacks if we truly have no items
    if (invoice.subtotal !== undefined && invoice.subtotal !== null) {
      return parseFloat(invoice.subtotal) || 0;
    }

    // Last resort
    return parseFloat(invoice.totalAmount || invoice.total) || 0;
  };

  const calculateDiscount = () => {
    // If we have a discount rate, always calculate from it
    if (!invoice.discountRate || invoice.discountRate === 0) {
      // Only use discountAmount if there's no discountRate
      if (invoice.discountAmount !== undefined && invoice.discountAmount !== null) {
        return parseFloat(invoice.discountAmount) || 0;
      }
      return 0;
    }

    // Calculate discount based on rate and type
    if (invoice.discountType === "FIXED" || invoice.discountType === "Fixed") {
      return parseFloat(invoice.discountRate) || 0;
    } else {
      // Percentage discount - always calculate from raw subtotal
      const subtotal = calculateRawSubtotal();
      const discount = (subtotal * parseFloat(invoice.discountRate)) / 100;
      return discount;
    }
  };

  const calculateTax = () => {
    // Use tax from invoice if available
    if (invoice.tax !== undefined && invoice.tax !== null) {
      return parseFloat(invoice.tax) || 0;
    }
    if (invoice.taxAmount !== undefined && invoice.taxAmount !== null) {
      return parseFloat(invoice.taxAmount) || 0;
    }
    // Otherwise calculate if tax is enabled
    if (!template.isTaxValue) return 0;
    const subtotal = calculateRawSubtotal();
    const discount = calculateDiscount();
    return (subtotal - discount) * 0.14; // 14% VAT
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateRawSubtotal();
    const discount = calculateDiscount();
    const tax = calculateTax();
    const total = subtotal - discount + tax;
    return total;
  };

  const calculateTotal = () => {
    // Use calculated total when we have items, otherwise use backend total
    const calculatedTotal = calculateFinalTotal();
    const finalTotal = (invoice.items && invoice.items.length > 0)
      ? calculatedTotal
      : (parseFloat(invoice.totalAmount) || calculatedTotal);
    return finalTotal;
  };

  // Add adjusted total calculation including returns
  const calculateAdjustedTotal = () => {
    const total = calculateTotal();
    const returns = parseFloat(invoice.totalReturns) || 0;
    return total - returns;
  };

  const calculateBalance = () => {
    // Calculate balance based on whether there are returns or not
    // If there are returns, use the adjusted total (after returns)
    // Otherwise use the regular total
    const returns = parseFloat(invoice.totalReturns) || 0;
    const baseTotal = returns > 0 ? calculateAdjustedTotal() : calculateTotal();
    const paid = parseFloat(invoice.amountPaid || invoice.paidAmount) || 0;
    return baseTotal - paid; // Can be negative for overpayments
  };


  // Print directly
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintHTML();
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  // Generate HTML for printing (matching Android layout exactly)
  const generatePrintHTML = () => {
    const dir = isRTL ? "rtl" : "ltr";
    const subtotal = calculateRawSubtotal();
    const discount = calculateDiscount();
    const tax = calculateTax();
    const total = calculateTotal();
    const returns = parseFloat(invoice.totalReturns) || 0;
    const adjustedTotal = calculateAdjustedTotal();
    const balance = calculateBalance();
    const amountPaid = parseFloat(invoice.amountPaid || invoice.paidAmount) || 0;

    // Determine invoice type
    const invoiceType = invoice.type === 'expense' || invoice.invoiceType === 'expense' || invoice.source === 'expense'
      ? (isRTL ? "فاتورة شراء" : "Purchase Invoice")
      : (isRTL ? "فاتورة مبيعات" : "Sales Invoice");

    return `
      <!DOCTYPE html>
      <html dir="${dir}">
      <head>
        <meta charset="UTF-8">
        <title>${invoiceType} #${invoice.invoiceNumber || invoice.id}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            direction: ${dir};
            color: #000;
            font-size: 13px;
          }
          .invoice-container {
            max-width: ${isRTL ? '720px' : '750px'};
            margin: 0 auto;
            ${isRTL ? 'padding-right: 10px;' : ''}
          }

          /* Header Layout */
          .header-container {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #000;
          }

          /* Company Header */
          .company-header {
            text-align: ${isRTL ? "right" : "left"};
            ${isRTL ? "order: 1;" : "order: 2;"}
          }
          .company-name {
            font-size: 26px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #000;
          }
          .company-detail {
            font-size: 14px;
            margin: 3px 0;
            color: #333;
          }
          .company-detail span {
            font-weight: normal;
          }

          /* Invoice Info Box */
          .invoice-info-box {
            ${isRTL ? "order: 2;" : "order: 1;"}
            text-align: ${isRTL ? "left" : "right"};
          }
          .invoice-type {
            font-size: 22px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .invoice-info-row {
            font-size: 14px;
            margin: 4px 0;
          }
          .invoice-info-row strong {
            display: inline-block;
            min-width: 110px;
            font-weight: bold;
          }

          /* Customer Section */
          .customer-section {
            margin: 20px 0;
          }
          .customer-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .customer-detail {
            font-size: 14px;
            margin: 3px 0;
          }
          .customer-detail strong {
            display: inline-block;
            min-width: 80px;
            font-weight: bold;
          }

          /* Table */
          table {
            width: 100%;
            border: 1px solid #ccc;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th {
            background-color: #f0f0f0;
            padding: 8px 10px;
            text-align: ${isRTL ? "right" : "left"};
            font-weight: bold;
            border: 1px solid #ccc;
            font-size: 13px;
          }
          td {
            padding: 7px 10px;
            border: 1px solid #ccc;
            text-align: ${isRTL ? "right" : "left"};
            font-size: 13px;
          }

          /* Totals */
          .totals-section {
            margin: 20px 0;
            margin-${isRTL ? "right" : "left"}: auto;
            width: fit-content;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
            font-size: 14px;
            min-width: 250px;
          }
          .total-label {
            text-align: ${isRTL ? "right" : "left"};
            padding-${isRTL ? "left" : "right"}: 25px;
          }
          .total-value {
            text-align: ${isRTL ? "left" : "right"};
            font-weight: bold;
            min-width: 100px;
          }
          .grand-total {
            font-size: 15px;
            font-weight: bold;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 2px solid #000;
            background: #f9f9f9;
            padding: 10px;
            border-radius: 2px;
          }
          .grand-total .total-value {
            color: #000;
            font-size: 16px;
          }

          /* Signature Section */
          .signature-section {
            display: flex;
            justify-content: space-around;
            margin-top: 80px;
            margin-bottom: 50px;
            page-break-inside: avoid;
          }
          .signature-block {
            text-align: center;
            min-width: 150px;
          }
          .signature-line {
            border-bottom: 1px solid #000;
            height: 35px;
            margin-bottom: 8px;
            width: 150px;
            display: inline-block;
          }
          .signature-label {
            font-size: 14px;
            font-weight: bold;
          }

          /* Footer */
          .footer {
            position: fixed;
            bottom: 10mm;
            left: 0;
            right: 0;
            padding: 10px 0;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
            background: white;
          }

          /* Page wrapper */
          .page-wrapper {
            position: relative;
            min-height: 297mm;
            padding-bottom: 30mm;
          }

          .invoice-content {
            position: relative;
          }

          .invoice-container {
            padding-bottom: 100px;
          }

          @media print {
            @page {
              size: A4;
              margin: 10mm 12mm 20mm 12mm;
            }
            body {
              padding: 0;
              margin: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .page-wrapper {
              min-height: 267mm;
              position: relative;
            }
            .invoice-container {
              padding-bottom: 120px;
            }
            .signature-section {
              position: absolute;
              bottom: 35mm;
              left: 0;
              right: 0;
              display: flex;
              justify-content: space-around;
              page-break-inside: avoid;
            }
            .footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              padding: 8px 0;
              background: white;
            }
          }
        </style>
      </head>
      <body>
        <div class="page-wrapper">
          <div class="invoice-content">
            <div class="invoice-container">

            <!-- Header Container with Two Columns -->
            <div class="header-container">
              <!-- Invoice Info Box (Left in RTL, Right in LTR) -->
              <div class="invoice-info-box">
                <div class="invoice-type">${invoiceType}</div>
                <div class="invoice-info-row">
                  <strong>${isRTL ? "رقم الفاتورة:" : "Invoice Number:"}</strong> ${invoice.invoiceNumber || invoice.number || invoice.id || ""}
                </div>
                <div class="invoice-info-row">
                  <strong>${isRTL ? "التاريخ:" : "Date:"}</strong> ${formatDate(invoice.invoiceDate || invoice.date || invoice.createdAt)}
                </div>
                ${invoice.dueDate ? `
                <div class="invoice-info-row">
                  <strong>${isRTL ? "تاريخ الاستحقاق:" : "Due Date:"}</strong> ${formatDate(invoice.dueDate)}
                </div>` : ''}
              </div>

              <!-- Company Header (Right in RTL, Left in LTR) -->
              <div class="company-header">
                ${template.isCompanyName && template.companyName ?
                  `<div class="company-name">${template.companyName}</div>` : ''}
                ${template.isCompanyAddress && template.companyAddress ?
                  `<div class="company-detail">${isRTL ? "العنوان:" : "Address:"} <span>${template.companyAddress}</span></div>` : ''}
                ${template.isMobileNumber && template.mobileNumber ?
                  `<div class="company-detail">${isRTL ? "الهاتف:" : "Phone:"} <span>${template.mobileNumber}</span></div>` : ''}
                ${template.isTaxNumber && template.taxNumber ?
                  `<div class="company-detail">${isRTL ? "الرقم الضريبي:" : "Tax Number:"} <span>${template.taxNumber}</span></div>` : ''}
                ${template.isCommercialNumber && template.commercialNumber ?
                  `<div class="company-detail">${isRTL ? "السجل التجاري:" : "Commercial Register:"} <span>${template.commercialNumber}</span></div>` : ''}
              </div>
            </div>

          <!-- Customer Section -->
          <div class="customer-section">
            <div class="customer-title">${isRTL ? "بيانات العميل" : "Customer Details"}</div>
            <div class="customer-detail">
              <strong>${isRTL ? "الاسم:" : "Name:"}</strong> ${entity?.name || invoice.businessEntity?.name || invoice.supplier?.name || invoice.client?.name || invoice.accountName || ""}
            </div>
            ${(entity?.address || invoice.businessEntity?.address || invoice.supplier?.address || invoice.client?.address) ? `
            <div class="customer-detail">
              <strong>${isRTL ? "العنوان:" : "Address:"}</strong> ${entity?.address || invoice.businessEntity?.address || invoice.supplier?.address || invoice.client?.address}
            </div>` : ''}
            ${(entity?.phone || invoice.businessEntity?.phone || invoice.supplier?.phone || invoice.client?.phone) ? `
            <div class="customer-detail">
              <strong>${isRTL ? "الهاتف:" : "Phone:"}</strong> ${entity?.phone || invoice.businessEntity?.phone || invoice.supplier?.phone || invoice.client?.phone}
            </div>` : ''}
            ${(entity?.email || invoice.businessEntity?.email || invoice.supplier?.email || invoice.client?.email) ? `
            <div class="customer-detail">
              <strong>${isRTL ? "البريد الإلكتروني:" : "Email:"}</strong> ${entity?.email || invoice.businessEntity?.email || invoice.supplier?.email || invoice.client?.email}
            </div>` : ''}
          </div>

          <!-- Items Table -->
          <table>
            <thead>
              <tr>
                ${isRTL ? `
                <th style="text-align: right;">${"المنتج"}</th>
                <th style="text-align: center;">${"الكمية"}</th>
                <th style="text-align: center;">${"سعر الوحدة"}</th>
                <th style="text-align: left;">${"الإجمالي"}</th>
                ` : `
                <th style="text-align: left;">${"Item"}</th>
                <th style="text-align: center;">${"Quantity"}</th>
                <th style="text-align: center;">${"Unit Price"}</th>
                <th style="text-align: right;">${"Total"}</th>
                `}
              </tr>
            </thead>
            <tbody>
              ${invoice.items?.map(item => {
                const itemName = isRTL
                  ? (item.itemNameAr || item.nameAr || item.name || item.productNameAr || item.productName || item.description || item.invoiceDetails?.description)
                  : (item.itemNameEn || item.nameEn || item.name || item.productNameEn || item.productName || item.description || item.invoiceDetails?.description);
                const quantity = parseFloat(item.quantity || item.invoiceDetails?.quantity) || 1;
                const price = parseFloat(item.pricePerUnit || item.invoiceDetails?.pricePerUnit || item.price || item.unitPrice) || 0;
                const itemTotal = parseFloat(item.totalAmount || item.invoiceDetails?.totalAmount) || (quantity * price);

                return isRTL ? `
                  <tr>
                    <td style="text-align: right;">${itemName || item.description || ''}</td>
                    <td style="text-align: center;">${quantity}</td>
                    <td style="text-align: center;">${formatCurrency(price)}</td>
                    <td style="text-align: left;">${formatCurrency(itemTotal)}</td>
                  </tr>
                ` : `
                  <tr>
                    <td style="text-align: left;">${itemName || item.description || ''}</td>
                    <td style="text-align: center;">${quantity}</td>
                    <td style="text-align: center;">${formatCurrency(price)}</td>
                    <td style="text-align: right;">${formatCurrency(itemTotal)}</td>
                  </tr>
                `;
              }).join('') || ''}
            </tbody>
          </table>

          <!-- Totals Section -->
          <div class="totals-section">
            <div class="total-row">
              <span class="total-label">${isRTL ? "الإجمالي" : "Subtotal"}</span>
              <span class="total-value">${formatCurrency(subtotal)}</span>
            </div>
            ${discount > 0 ? `
            <div class="total-row">
              <span class="total-label">${isRTL ? "الخصم" : "Discount"}${invoice.discountType !== "FIXED" && invoice.discountType !== "Fixed" && invoice.discountRate ? ` (${invoice.discountRate}%)` : ''}</span>
              <span class="total-value">-${formatCurrency(discount)}</span>
            </div>` : ''}
            ${template.isTaxValue ? `
            <div class="total-row">
              <span class="total-label">${isRTL ? "الضريبة (14%)" : "Tax (14%)"}</span>
              <span class="total-value">${formatCurrency(tax)}</span>
            </div>` : ''}
            <div class="total-row grand-total">
              <span class="total-label">${isRTL ? "المجموع الكلي" : "Total Amount"}</span>
              <span class="total-value">${formatCurrency(total)}</span>
            </div>
            ${returns > 0 ? `
            <div class="total-row">
              <span class="total-label">${isRTL ? "المرتجعات" : "Returns"}</span>
              <span class="total-value">-${formatCurrency(returns)}</span>
            </div>
            <div class="total-row grand-total">
              <span class="total-label">${isRTL ? "الإجمالي بعد المرتجعات" : "Adjusted Total"}</span>
              <span class="total-value">${formatCurrency(adjustedTotal)}</span>
            </div>` : ''}
            ${template.isPaymentDetails ? `
            <div class="total-row">
              <span class="total-label">${isRTL ? "المدفوع" : "Amount Paid"}</span>
              <span class="total-value">${formatCurrency(amountPaid)}</span>
            </div>
            <div class="total-row">
              <span class="total-label">${isRTL ? "المتبقى" : "Balance Due"}</span>
              <span class="total-value">${formatCurrency(balance)}</span>
            </div>` : ''}
          </div>

          ${invoice.notes ? `
          <div class="notes-section" style="margin: 25px 0;">
            <h4 style="font-size: 16px; margin-bottom: 10px; font-weight: bold;">${isRTL ? "ملاحظات" : "Notes"}</h4>
            <p style="font-size: 14px; color: #555; line-height: 1.5;">${invoice.notes}</p>
          </div>` : ''}

            </div>
          </div>

          <!-- Signature Section - Positioned absolutely -->
          <div class="signature-section">
            <div class="signature-block">
              <div class="signature-line"></div>
              <div class="signature-label">${isRTL ? "التوقيع" : "Signature"}</div>
            </div>
            <div class="signature-block">
              <div class="signature-line"></div>
              <div class="signature-label">${isRTL ? "الختم" : "Seal"}</div>
            </div>
          </div>

          ${template.isFooter ? `
          <div class="footer">
            <p>${isRTL ? "تم إصدار هذه الفاتورة بواسطة تطبيق ايجرو كير برو" : "This invoice was generated by EgroCare Pro"}</p>
          </div>` : ''}
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{t("Print Settings")}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Settings Panel */}
          <div className={styles.settingsPanel}>
            <div className={styles.section}>
              <h3>{t("Company Details")}</h3>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldHeader}>
                  <label>{t("Company Name")}</label>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isCompanyName')}
                  >
                    {template.isCompanyName ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
                <input
                  type="text"
                  value={template.companyName}
                  onChange={(e) => updateTemplateField('companyName', e.target.value)}
                  placeholder={t("Enter company name")}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldHeader}>
                  <label>{t("Company Address")}</label>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isCompanyAddress')}
                  >
                    {template.isCompanyAddress ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
                <textarea
                  value={template.companyAddress}
                  onChange={(e) => updateTemplateField('companyAddress', e.target.value)}
                  placeholder={t("Enter company address")}
                  className={styles.textarea}
                  rows="2"
                />
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldHeader}>
                  <label>{t("Phone Number")}</label>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isMobileNumber')}
                  >
                    {template.isMobileNumber ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
                <input
                  type="tel"
                  value={template.mobileNumber}
                  onChange={(e) => updateTemplateField('mobileNumber', e.target.value)}
                  placeholder={t("Enter phone number")}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldHeader}>
                  <label>{t("Tax Number")}</label>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isTaxNumber')}
                  >
                    {template.isTaxNumber ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
                <input
                  type="text"
                  value={template.taxNumber}
                  onChange={(e) => updateTemplateField('taxNumber', e.target.value)}
                  placeholder={t("Enter tax number")}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldHeader}>
                  <label>{t("Commercial Register")}</label>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isCommercialNumber')}
                  >
                    {template.isCommercialNumber ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
                <input
                  type="text"
                  value={template.commercialNumber}
                  onChange={(e) => updateTemplateField('commercialNumber', e.target.value)}
                  placeholder={t("Enter commercial register")}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h3>{t("Display Options")}</h3>


              <div className={styles.optionItem}>
                <span>{t("Tax (14%)")}</span>
                <div className={styles.toggleOption}>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isTaxValue')}
                  >
                    {template.isTaxValue ? <FaEye /> : <FaEyeSlash />}
                  </button>
                  <span>{template.isTaxValue ? t("Show") : t("Hide")}</span>
                </div>
              </div>

              <div className={styles.optionItem}>
                <span>{t("Payment Details")}</span>
                <div className={styles.toggleOption}>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isPaymentDetails')}
                  >
                    {template.isPaymentDetails ? <FaEye /> : <FaEyeSlash />}
                  </button>
                  <span>{template.isPaymentDetails ? t("Show") : t("Hide")}</span>
                </div>
              </div>

              <div className={styles.optionItem}>
                <span>{t("Footer")}</span>
                <div className={styles.toggleOption}>
                  <button
                    className={styles.visibilityToggle}
                    onClick={() => toggleVisibility('isFooter')}
                  >
                    {template.isFooter ? <FaEye /> : <FaEyeSlash />}
                  </button>
                  <span>{template.isFooter ? t("Show") : t("Hide")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className={styles.previewPanel}>
            <div className={styles.previewHeader}>
              <h3>{t("Preview")}</h3>
            </div>

            <div className={styles.previewContent}>
              <div
                ref={previewRef}
                className={styles.invoicePreview}
                style={{
                  direction: isRTL ? 'rtl' : 'ltr',
                  fontFamily: 'Arial, sans-serif',
                  padding: '40px',
                  maxWidth: isRTL ? '720px' : '750px',
                  margin: '0 auto'
                }}
              >
                {/* Generate preview using exact same HTML as print */}
                <div dangerouslySetInnerHTML={{ __html: `
                  <!-- Header Container with Two Columns -->
                  <div style="display: flex; justify-content: space-between; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #000;">
                    <!-- Invoice Info Box (Left in RTL, Right in LTR) -->
                    <div style="${isRTL ? 'order: 2;' : 'order: 1;'} text-align: ${isRTL ? 'left' : 'right'};">
                      <div style="font-size: 22px; font-weight: bold; margin-bottom: 10px;">${invoice.type === 'expense' || invoice.invoiceType === 'expense' || invoice.source === 'expense' ? (isRTL ? 'فاتورة شراء' : 'Purchase Invoice') : (isRTL ? 'فاتورة مبيعات' : 'Sales Invoice')}</div>
                      <div style="font-size: 14px; margin: 4px 0;">
                        <strong style="display: inline-block; min-width: 110px; font-weight: bold;">${isRTL ? 'رقم الفاتورة:' : 'Invoice Number:'}</strong> ${invoice.invoiceNumber || invoice.number || invoice.id || ''}
                      </div>
                      <div style="font-size: 14px; margin: 4px 0;">
                        <strong style="display: inline-block; min-width: 110px; font-weight: bold;">${isRTL ? 'التاريخ:' : 'Date:'}</strong> ${formatDate(invoice.invoiceDate || invoice.date || invoice.createdAt)}
                      </div>
                      ${invoice.dueDate ? `
                      <div style="font-size: 14px; margin: 4px 0;">
                        <strong style="display: inline-block; min-width: 110px; font-weight: bold;">${isRTL ? 'تاريخ الاستحقاق:' : 'Due Date:'}</strong> ${formatDate(invoice.dueDate)}
                      </div>` : ''}
                    </div>

                    <!-- Company Header (Right in RTL, Left in LTR) -->
                    <div style="${isRTL ? 'order: 1;' : 'order: 2;'} text-align: ${isRTL ? 'right' : 'left'};">
                      ${template.isCompanyName && template.companyName ? `<div style="font-size: 26px; font-weight: bold; margin-bottom: 10px; color: #000;">${template.companyName}</div>` : ''}
                      ${template.isCompanyAddress && template.companyAddress ? `<div style="font-size: 14px; margin: 3px 0; color: #333;">${isRTL ? 'العنوان:' : 'Address:'} <span style="font-weight: normal;">${template.companyAddress}</span></div>` : ''}
                      ${template.isMobileNumber && template.mobileNumber ? `<div style="font-size: 14px; margin: 3px 0; color: #333;">${isRTL ? 'الهاتف:' : 'Phone:'} <span style="font-weight: normal;">${template.mobileNumber}</span></div>` : ''}
                      ${template.isTaxNumber && template.taxNumber ? `<div style="font-size: 14px; margin: 3px 0; color: #333;">${isRTL ? 'الرقم الضريبي:' : 'Tax Number:'} <span style="font-weight: normal;">${template.taxNumber}</span></div>` : ''}
                      ${template.isCommercialNumber && template.commercialNumber ? `<div style="font-size: 14px; margin: 3px 0; color: #333;">${isRTL ? 'السجل التجاري:' : 'Commercial Register:'} <span style="font-weight: normal;">${template.commercialNumber}</span></div>` : ''}
                    </div>
                  </div>

                  <!-- Customer Section -->
                  <div style="margin: 20px 0;">
                    <div style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${isRTL ? 'بيانات العميل' : 'Customer Details'}</div>
                    <div style="font-size: 14px; margin: 3px 0;">
                      <strong style="display: inline-block; min-width: 80px; font-weight: bold;">${isRTL ? 'الاسم:' : 'Name:'}</strong> ${entity?.name || invoice.businessEntity?.name || invoice.supplier?.name || invoice.client?.name || invoice.accountName || ''}
                    </div>
                    ${(entity?.address || invoice.businessEntity?.address || invoice.supplier?.address || invoice.client?.address) ? `
                    <div style="font-size: 14px; margin: 3px 0;">
                      <strong style="display: inline-block; min-width: 80px; font-weight: bold;">${isRTL ? 'العنوان:' : 'Address:'}</strong> ${entity?.address || invoice.businessEntity?.address || invoice.supplier?.address || invoice.client?.address}
                    </div>` : ''}
                    ${(entity?.phone || invoice.businessEntity?.phone || invoice.supplier?.phone || invoice.client?.phone) ? `
                    <div style="font-size: 14px; margin: 3px 0;">
                      <strong style="display: inline-block; min-width: 80px; font-weight: bold;">${isRTL ? 'الهاتف:' : 'Phone:'}</strong> ${entity?.phone || invoice.businessEntity?.phone || invoice.supplier?.phone || invoice.client?.phone}
                    </div>` : ''}
                    ${(entity?.email || invoice.businessEntity?.email || invoice.supplier?.email || invoice.client?.email) ? `
                    <div style="font-size: 14px; margin: 3px 0;">
                      <strong style="display: inline-block; min-width: 80px; font-weight: bold;">${isRTL ? 'البريد الإلكتروني:' : 'Email:'}</strong> ${entity?.email || invoice.businessEntity?.email || invoice.supplier?.email || invoice.client?.email}
                    </div>` : ''}
                  </div>

                  <!-- Items Table -->
                  <table style="width: 100%; border: 1px solid #ccc; border-collapse: collapse; margin: 20px 0;">
                    <thead>
                      <tr>
                        ${isRTL ? `
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: right; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">المنتج</th>
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: center; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">الكمية</th>
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: center; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">سعر الوحدة</th>
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: left; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">الإجمالي</th>
                        ` : `
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: left; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">Item</th>
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: center; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">Quantity</th>
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: center; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">Unit Price</th>
                        <th style="background-color: #f0f0f0; padding: 8px 10px; text-align: right; font-weight: bold; border: 1px solid #ccc; font-size: 13px;">Total</th>
                        `}
                      </tr>
                    </thead>
                    <tbody>
                      ${invoice.items?.map(item => {
                        const itemName = isRTL
                          ? (item.itemNameAr || item.nameAr || item.name || item.productNameAr || item.productName || item.description || item.invoiceDetails?.description)
                          : (item.itemNameEn || item.nameEn || item.name || item.productNameEn || item.productName || item.description || item.invoiceDetails?.description);
                        const quantity = parseFloat(item.quantity || item.invoiceDetails?.quantity) || 1;
                        const price = parseFloat(item.pricePerUnit || item.invoiceDetails?.pricePerUnit || item.price || item.unitPrice) || 0;
                        const itemTotal = parseFloat(item.totalAmount || item.invoiceDetails?.totalAmount) || (quantity * price);

                        return isRTL ? `
                          <tr>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: right; font-size: 13px;">${itemName || item.description || ''}</td>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: center; font-size: 13px;">${quantity}</td>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: center; font-size: 13px;">${formatCurrency(price)}</td>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: left; font-size: 13px;">${formatCurrency(itemTotal)}</td>
                          </tr>
                        ` : `
                          <tr>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: left; font-size: 13px;">${itemName || item.description || ''}</td>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: center; font-size: 13px;">${quantity}</td>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: center; font-size: 13px;">${formatCurrency(price)}</td>
                            <td style="padding: 7px 10px; border: 1px solid #ccc; text-align: right; font-size: 13px;">${formatCurrency(itemTotal)}</td>
                          </tr>
                        `;
                      }).join('') || ''}
                    </tbody>
                  </table>

                  <!-- Totals Section -->
                  <div style="margin: 20px 0; margin-${isRTL ? 'right' : 'left'}: auto; width: fit-content;">
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px; min-width: 250px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 25px;">${isRTL ? 'الإجمالي' : 'Subtotal'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px;">${formatCurrency(calculateRawSubtotal())}</span>
                    </div>
                    ${calculateDiscount() > 0 ? `
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px; min-width: 250px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 25px;">${isRTL ? 'الخصم' : 'Discount'}${invoice.discountType !== 'FIXED' && invoice.discountType !== 'Fixed' && invoice.discountRate ? ` (${invoice.discountRate}%)` : ''}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px;">-${formatCurrency(calculateDiscount())}</span>
                    </div>` : ''}
                    ${template.isTaxValue ? `
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px; min-width: 250px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 25px;">${isRTL ? 'الضريبة (14%)' : 'Tax (14%)'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px;">${formatCurrency(calculateTax())}</span>
                    </div>` : ''}
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 15px; min-width: 250px; margin-top: 8px; padding-top: 8px; border-top: 2px solid #000; background: #f9f9f9; padding: 10px; border-radius: 2px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 15px; font-weight: bold;">${isRTL ? 'المجموع الكلي' : 'Total Amount'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px; font-size: 16px; color: #000;">${formatCurrency(calculateTotal())}</span>
                    </div>
                    ${(parseFloat(invoice.totalReturns) || 0) > 0 ? `
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px; min-width: 250px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 25px;">${isRTL ? 'المرتجعات' : 'Returns'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px;">-${formatCurrency(parseFloat(invoice.totalReturns) || 0)}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 15px; min-width: 250px; margin-top: 8px; padding-top: 8px; border-top: 2px solid #000; background: #f9f9f9; padding: 10px; border-radius: 2px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 15px; font-weight: bold;">${isRTL ? 'الإجمالي بعد المرتجعات' : 'Adjusted Total'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px; font-size: 16px; color: #000;">${formatCurrency(calculateAdjustedTotal())}</span>
                    </div>` : ''}
                    ${template.isPaymentDetails ? `
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px; min-width: 250px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 25px;">${isRTL ? 'المدفوع' : 'Amount Paid'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px;">${formatCurrency(parseFloat(invoice.amountPaid || invoice.paidAmount) || 0)}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px; min-width: 250px;">
                      <span style="text-align: ${isRTL ? 'right' : 'left'}; padding-${isRTL ? 'left' : 'right'}: 25px;">${isRTL ? 'المتبقى' : 'Balance Due'}</span>
                      <span style="text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; min-width: 100px;">${formatCurrency(calculateBalance())}</span>
                    </div>` : ''}
                  </div>

                  ${invoice.notes ? `
                  <div style="margin: 25px 0;">
                    <h4 style="font-size: 16px; margin-bottom: 10px; font-weight: bold;">${isRTL ? 'ملاحظات' : 'Notes'}</h4>
                    <p style="font-size: 14px; color: #555; line-height: 1.5;">${invoice.notes}</p>
                  </div>` : ''}

                  <!-- Signature Section -->
                  <div style="display: flex; justify-content: space-around; margin-top: 80px; margin-bottom: 50px;">
                    <div style="text-align: center; min-width: 150px;">
                      <div style="border-bottom: 1px solid #000; height: 35px; margin-bottom: 8px; width: 150px; display: inline-block;"></div>
                      <div style="font-size: 14px; font-weight: bold;">${isRTL ? 'التوقيع' : 'Signature'}</div>
                    </div>
                    <div style="text-align: center; min-width: 150px;">
                      <div style="border-bottom: 1px solid #000; height: 35px; margin-bottom: 8px; width: 150px; display: inline-block;"></div>
                      <div style="font-size: 14px; font-weight: bold;">${isRTL ? 'الختم' : 'Seal'}</div>
                    </div>
                  </div>

                  ${template.isFooter ? `
                  <div style="margin-top: 20px; padding: 10px 0; border-top: 1px solid #ddd; text-align: center; font-size: 12px; color: #666;">
                    <p>${isRTL ? 'تم إصدار هذه الفاتورة بواسطة تطبيق ايجرو كير برو' : 'This invoice was generated by EgroCare Pro'}</p>
                  </div>` : ''}
                ` }} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.printBtn}
            onClick={handlePrint}
          >
            <FaPrint /> {t("Print")}
          </button>

          <button
            className={styles.cancelBtn}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintModal;